import {useEffect} from 'react';

import type {RefObject} from 'react';

/** Applies damped scroll inertia to descendants marked data-scroll-tilt.
 * Owns only --float-* properties, so layout and carousel transforms can compose with it.
 * Panels must remain mounted; motion pauses offscreen and respects reduced motion.
 */
export const useScrollTilt = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const root = ref.current;
    if(!root) {
      return undefined;
    }
    const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-scroll-tilt]'));
    const states = panels.map(() => ({position: 0, velocity: 0}));
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let frame = 0;
    let lastTime = 0;
    let lastScroll = window.scrollY;
    const paint = (): void => {
      panels.forEach((panel, index) => {
        const displacement = states[index].position;
        const direction = index % 2 === 1 ? -1 : 1;
        panel.style.setProperty('--float-x', `${displacement}deg`);
        panel.style.setProperty('--float-y', `${displacement * direction * 0.65}deg`);
        panel.style.setProperty('--float-z', `${displacement * direction * 0.16}deg`);
        panel.style.setProperty('--float-offset', `${displacement * 1.4}px`);
      });
    };
    const stop = (): void => {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      states.forEach((state) => {
        state.position = 0;
        state.velocity = 0;
      });
      paint();
    };
    const tick = (time: number): void => {
      const dt = lastTime ? Math.min((time - lastTime) / 1000, 0.032) : 1 / 60;
      lastTime = time;
      let moving = false;
      states.forEach((state, index) => {
        const stiffness = 18 - ((index % 3) * 3);
        const damping = 5.8 - ((index % 3) * 0.45);
        state.velocity += ((-stiffness * state.position) - (damping * state.velocity)) * dt;
        state.position = Math.max(-10, Math.min(10, state.position + (state.velocity * dt)));
        if(Math.abs(state.position) < 0.005 && Math.abs(state.velocity) < 0.01) {
          state.position = 0;
          state.velocity = 0;
        } else {
          moving = true;
        }
      });
      paint();
      frame = moving ? requestAnimationFrame(tick) : 0;
      if(!moving) {
        lastTime = 0;
      }
    };
    const onScroll = (): void => {
      const delta = Math.max(-100, Math.min(100, window.scrollY - lastScroll));
      lastScroll = window.scrollY;
      if(!visible || preference.matches || document.hidden) {
        return;
      }
      states.forEach((state, index) => {
        state.velocity = Math.max(-32, Math.min(32, state.velocity + (delta * (0.15 + ((index % 3) * 0.035)))));
      });
      if(!frame) {
        frame = requestAnimationFrame(tick);
      }
    };
    const reset = (): void => {
      lastScroll = window.scrollY;
      stop();
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if(!visible) {
        stop();
      }
    });
    observer.observe(root);
    paint();
    window.addEventListener('scroll', onScroll, {passive: true});
    document.addEventListener('visibilitychange', reset);
    preference.addEventListener('change', reset);
    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', reset);
      preference.removeEventListener('change', reset);
    };
  }, [ref]);
};
